import { Box, BoxProps, makeStyles, Paper, PaperProps } from '@material-ui/core';
import { forwardRef, ReactNode } from 'react';

import { Loading } from '@/components/modules/Application/components/_shared/components/Loading';
import { CustomPalette } from '@/config/types';
import { useCustomTheme } from '@/hooks/useCustomTheme';

import { useScrollTop } from '../../modules/Application/hooks/useScrollTop';

export type FillColor =
  | 'inherit'
  | 'paper'
  | 'grey.light'
  | 'grey.dark'
  | 'success.light'
  | 'warning.light'
  | 'error.light'
  | 'info.light'
  | 'secondary.dark'
  | 'secondary.light';

export interface CustomPaperProps extends PaperProps {
  loading?: boolean;
  children?: ReactNode;
  fill?: FillColor;
  light?: boolean;
  boxProps?: BoxProps;
  hoverable?: boolean;
  hoverColor?: string;
}

const useStyles = makeStyles((theme) => ({
  paper: (props: Partial<CustomPaperProps>) => ({
    ...(props.hoverable && {
      cursor: 'pointer',
      transition: theme.transitions.create('background-color', {
        duration: theme.transitions.duration.short,
      }),
      ...(props.hoverable &&
        props.hoverColor && {
          '&:hover': {
            backgroundColor: `${props.hoverColor} !important`,
          },
        }),
    }),
  }),
}));

export const CustomPaper = forwardRef<HTMLDivElement, CustomPaperProps>(
  ({ loading = false, children, fill = 'inherit', boxProps, hoverable, hoverColor, ...props }, ref) => {
    const { palette, customBorderRadius } = useCustomTheme();

    useScrollTop();

    const fillColor = getFillColor(fill, palette);

    const classes = useStyles({ hoverable, hoverColor });

    return (
      <Box display={'flex'} width={'100%'} {...boxProps}>
        <Paper
          className={classes.paper}
          elevation={1}
          {...props}
          ref={ref}
          style={{
            flexGrow: 1,
            width: '100%',
            overflow: 'hidden',
            position: 'relative',
            borderRadius: customBorderRadius.paper,
            backgroundColor: fillColor,
            padding: 24,
            ...props?.style,
          }}
        >
          {loading ? <Loading /> : children}
        </Paper>
      </Box>
    );
  },
);

export const getFillColor = (fill: FillColor, palette: CustomPalette): string => {
  const colorMap: Record<FillColor, string> = {
    inherit: 'inherit',
    paper: palette.common.white,
    'secondary.dark': palette.secondary.dark,
    'secondary.light': palette.secondary.light,
    'success.light': palette.success.light,
    'warning.light': palette.warning.light,
    'error.light': palette.error.light,
    'info.light': palette.info.light,
    'grey.light': palette.grey[50],
    'grey.dark': palette.grey[100],
  };

  return colorMap[fill];
};
